import React, { useMemo, useState } from "react";
import { Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { Sort } from "assets";
import CustomButton from "components/CustomButton";
import { FormSelects } from "components/FormInputSelect";
import Pagination from "components/Pagination";
import Table from "components/Table";
import { actionColumnLayout, columnLayout } from "components/tableColumnLayout";

const PAGE_SIZE = 5;

// Penanda modal: tertutup, tambah baris baru, atau sunting baris ke-n (>= 0).
const MODAL_CLOSED = null;
const MODAL_ADD = -1;

// Disamakan dengan input teks di modal yang sama: tinggi, sudut, dan ukuran huruf.
const SELECT_STYLES = {
  indicatorSeparator: () => ({ display: "none" }),
  control: (provided) => ({ ...provided, minHeight: "52px", borderRadius: "8px" }),
  valueContainer: (provided) => ({ ...provided, padding: "0 16px" }),
  placeholder: (provided) => ({ ...provided, fontSize: "14px" }),
  singleValue: (provided) => ({ ...provided, fontSize: "14px" }),
  option: (provided) => ({ ...provided, fontSize: "14px" }),
};

const emptyValues = (fields) =>
  fields.reduce((values, field) => ({ ...values, [field.name]: "" }), {});

const hasValue = (value) => String(value ?? "").trim() !== "";

/**
 * Kartu berisi tabel kecil yang bisa ditambah, disunting, dihapus, diurutkan,
 * dan dipaginasi sepenuhnya di sisi klien. Dipakai untuk Aset ID dan Judul Lagu
 * Alias di pendaftaran & detail lagu.
 *
 * @param {object} props
 * @param {string} props.title  judul kartu
 * @param {string} props.addLabel  label tombol tambah di kartu
 * @param {string} props.addTitle  judul modal saat menambah
 * @param {string} props.editTitle  judul modal saat menyunting
 * @param {string} props.submitLabel  label tombol simpan di modal saat menambah
 * @param {string} props.idPrefix  awalan id elemen; wajib unik per halaman
 * @param {Array<{
 *   name: string,
 *   label: string,
 *   placeholder?: string,
 *   required?: boolean,
 *   type?: "text" | "select",
 *   options?: Array<{label: string, value: string}>
 * }>} props.fields  kolom tabel sekaligus isian modal
 * @param {Array<object>} props.items  satu objek per baris
 * @param {(items: Array<object>) => void} props.onChange  menerima array baru
 * @param {boolean} [props.isReadOnly]  sembunyikan tombol tambah & kolom aksi
 * @param {string} [props.emptyText]
 */
const LaguItemTable = ({
  title,
  addLabel,
  addTitle,
  editTitle,
  submitLabel,
  idPrefix,
  fields,
  items,
  onChange,
  isReadOnly = false,
  emptyText,
}) => {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ field: null, isDesc: false });
  const [modalIndex, setModalIndex] = useState(MODAL_CLOSED);
  const [formValues, setFormValues] = useState(() => emptyValues(fields));

  const isModalOpen = modalIndex !== MODAL_CLOSED;
  const isEditing = isModalOpen && modalIndex !== MODAL_ADD;

  // `index` menyimpan posisi asli baris di `items`, sehingga hapus/sunting tetap
  // tepat sasaran walau tabelnya sedang diurutkan dan dipotong per halaman.
  const sortedRows = useMemo(() => {
    const rows = items.map((item, index) => ({ ...item, id: index, index }));
    if (!sort.field) return rows;

    const sorted = [...rows].sort((a, b) =>
      String(a[sort.field] ?? "").localeCompare(String(b[sort.field] ?? ""), "id", {
        sensitivity: "base",
      })
    );
    return sort.isDesc ? sorted.reverse() : sorted;
  }, [items, sort]);

  const totalPage = Math.ceil(sortedRows.length / PAGE_SIZE);
  // Halaman aktif bisa melampaui batas setelah baris terakhir di halaman itu dihapus.
  const currentPage = Math.min(page, Math.max(totalPage, 1));
  const pageRows = sortedRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Field bertanda `required` wajib terisi, dan baris yang seluruh isiannya
  // kosong tidak boleh disimpan — termasuk untuk modal yang field-nya tidak
  // bertanda wajib, seperti Judul Alias.
  const isFormValid =
    fields.every((field) => !field.required || hasValue(formValues[field.name])) &&
    fields.some((field) => hasValue(formValues[field.name]));

  const toggleSort = (field) =>
    setSort((prev) => ({
      field,
      isDesc: prev.field === field ? !prev.isDesc : false,
    }));

  const openModal = (index) => {
    setFormValues(
      index === MODAL_ADD
        ? emptyValues(fields)
        : { ...emptyValues(fields), ...items[index] }
    );
    setModalIndex(index);
  };

  const closeModal = () => setModalIndex(MODAL_CLOSED);

  const setFieldValue = (name, value) =>
    setFormValues((prev) => ({ ...prev, [name]: value }));

  const handleDelete = (index) => onChange(items.filter((_, i) => i !== index));

  const handleSave = () => {
    if (!isFormValid) return;

    const value = fields.reduce(
      (values, field) => ({
        ...values,
        [field.name]: String(formValues[field.name] ?? "").trim(),
      }),
      {}
    );

    const nextItems = isEditing
      ? items.map((item, i) => (i === modalIndex ? { ...item, ...value } : item))
      : [...items, value];

    onChange(nextItems);

    // Baris baru selalu masuk di akhir daftar; bila tabel tidak sedang diurutkan,
    // pindah ke halaman terakhir supaya baris itu langsung terlihat.
    if (!isEditing && !sort.field) {
      setPage(Math.ceil(nextItems.length / PAGE_SIZE));
    }
    closeModal();
  };

  const renderField = (field) => {
    const inputId = `${idPrefix}-${field.name}`;

    return (
      <div key={field.name} className="lagu-item-modal-field">
        <Label for={inputId}>
          {field.label}
          {field.required && <span className="required-mark">*</span>}
        </Label>
        {field.type === "select" ? (
          <FormSelects
            inputId={inputId}
            name={field.name}
            placeholder={field.placeholder}
            options={field.options}
            value={formValues[field.name]}
            onChange={(option) => setFieldValue(field.name, option.value)}
            styles={SELECT_STYLES}
          />
        ) : (
          <Input
            id={inputId}
            name={field.name}
            placeholder={field.placeholder}
            value={formValues[field.name]}
            onChange={(e) => setFieldValue(field.name, e.target.value)}
          />
        )}
      </div>
    );
  };

  const columns = [
    ...fields.map((field) => ({
      dataField: field.name,
      text: (
        <button
          type="button"
          className="lagu-sort-header"
          onClick={() => toggleSort(field.name)}
          aria-label={`Urutkan berdasarkan ${field.label}`}
        >
          <span>{field.label}</span>
          <Sort />
        </button>
      ),
      headerStyle: columnLayout(1),
      style: columnLayout(1),
      // Kolom pilihan menyimpan kode (mis. '05'), bukan namanya. Tanpa ini
      // tabel menampilkan kodenya dan barisnya jadi tidak terbaca.
      //
      // `formatExtraData` wajib: react-bootstrap-table hanya me-render ulang
      // sel bila nilai barisnya atau formatExtraData berubah (lihat
      // shouldComponentUpdate di lib/src/cell.js). Daftar opsi datang belakangan
      // dari API, jadi tanpa ini sel yang sudah tergambar tetap menampilkan
      // kodenya selamanya.
      ...(field.type === 'select' && {
        formatExtraData: field.options,
        formatter: (cell) => {
          const option = (field.options || []).find((x) => x.value === cell);
          return option ? option.label : cell;
        },
      }),
    })),
    ...(isReadOnly
      ? []
      : [
          {
            dataField: "action",
            isDummyField: true,
            text: "Action",
            headerStyle: actionColumnLayout(),
            style: actionColumnLayout(),
            // react-bootstrap-table hanya me-render ulang sel dummy bila
            // formatExtraData berubah. Tanpa ini tombolnya menggenggam `items`
            // versi lama, dan hapus/sunting bisa mengenai baris yang salah.
            formatExtraData: items,
            formatter: (cell, row) => {
              const rowLabel = row[fields[0].name];
              return (
                <div className="lagu-row-actions">
                  <button
                    type="button"
                    className="lagu-action-btn is-delete"
                    aria-label={`Hapus ${rowLabel}`}
                    onClick={() => handleDelete(row.index)}
                  >
                    <FiTrash2 />
                  </button>
                  <button
                    type="button"
                    className="lagu-action-btn is-edit"
                    aria-label={`Sunting ${rowLabel}`}
                    onClick={() => openModal(row.index)}
                  >
                    <FiEdit2 />
                  </button>
                </div>
              );
            },
          },
        ]),
  ];

  return (
    <section className="form-wrapper lagu-card">
      <div className="lagu-card-header">
        <h3>{title}</h3>
        {!isReadOnly && (
          <CustomButton
            label={addLabel}
            color="primary-btn"
            className="lagu-card-add"
            onClick={() => openModal(MODAL_ADD)}
          />
        )}
      </div>

      <div className="lagu-card-table">
        <Table columnRefs={columns} dataTable={pageRows} noDataText={emptyText} />
        <Pagination
          variant="compact"
          isPage={currentPage}
          isTotalPage={totalPage}
          handleClickNumber={setPage}
        />
      </div>

      {/* Header sengaja tanpa tombol ×, sesuai desain; modal tetap bisa ditutup
          lewat Cancel, klik di luar modal, atau tombol Esc. */}
      <Modal
        isOpen={isModalOpen}
        toggle={closeModal}
        centered
        className="lagu-item-modal-dialog"
        backdropClassName="modal-backdrop-blur"
      >
        <ModalHeader>{isEditing ? editTitle : addTitle}</ModalHeader>
        <ModalBody className="lagu-item-modal">{fields.map(renderField)}</ModalBody>
        <ModalFooter>
          <CustomButton
            label="Cancel"
            className="lagu-modal-cancel"
            onClick={closeModal}
          />
          <CustomButton
            label={isEditing ? "Save" : submitLabel}
            // Tetap berwarna merek saat nonaktif (hanya diredupkan), sesuai desain.
            color="primary-btn"
            className="lagu-modal-submit"
            onClick={handleSave}
            disabled={!isFormValid}
          />
        </ModalFooter>
      </Modal>
    </section>
  );
};

export default LaguItemTable;
